/**
 * Buttons. One filled `primary` per view; `secondary` (outlined) for the
 * rest; `ghost` for quiet actions in card headers and toolbars; `danger`
 * for destructive ones. ButtonLink is the same look on a router link, and
 * IconButton is a square button whose `label` names it for screen readers.
 */

import { Link } from "react-router-dom";
import { Icon, Spinner } from "./Icon.jsx";
import { ICON_SIZE, buttonClass } from "./buttonStyles.js";

function Content({ icon, iconRight, loading, size, children }) {
  const s = ICON_SIZE[size] ?? ICON_SIZE.md;
  return (
    <>
      {loading ? <Spinner className={s} /> : icon && <Icon name={icon} className={s} />}
      {children}
      {iconRight && <Icon name={iconRight} className={s} />}
    </>
  );
}

/**
 * `primary` and `small` are the older spelling of `variant="primary"` and
 * `size="xs"`, kept so existing callers read the same.
 */
export function Button({ variant, size, primary = false, small = false, icon, iconRight, loading = false, block, className, children, type = "button", disabled, ...rest }) {
  const v = variant ?? (primary ? "primary" : "secondary");
  const s = size ?? (small ? "xs" : "sm");
  return (
    <button type={type} disabled={disabled || loading} aria-busy={loading || undefined} className={buttonClass({ variant: v, size: s, block, className })} {...rest}>
      <Content icon={icon} iconRight={iconRight} loading={loading} size={s}>
        {children}
      </Content>
    </button>
  );
}

export function ButtonLink({ to, variant = "secondary", size = "sm", icon, iconRight, className, children, ...rest }) {
  return (
    <Link to={to} className={buttonClass({ variant, size, className })} {...rest}>
      <Content icon={icon} iconRight={iconRight} size={size}>
        {children}
      </Content>
    </Link>
  );
}

const ICON_BUTTON_TONES = {
  default: "text-ink-3 hover:bg-surface-2 hover:text-ink",
  sidebar: "text-side-ink-3 hover:bg-side-hover hover:text-side-ink",
};

export function IconButton({ icon, label, size = "md", tone = "default", className = "", title, ...rest }) {
  const box = size === "lg" ? "size-12" : size === "sm" ? "size-8" : "size-10";
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      className={`inline-flex shrink-0 items-center justify-center rounded-control transition-colors ${box} ${ICON_BUTTON_TONES[tone] ?? ICON_BUTTON_TONES.default} ${className}`}
      {...rest}
    >
      <Icon name={icon} className={size === "lg" ? "size-5" : "size-4"} />
    </button>
  );
}
